/**
 * Client entry point for React Router.
 * Environment variables are accessed via import.meta.env (Vite)
 */

import { HydratedRouter } from "react-router/dom";
import { useLocation, useMatches } from "react-router";
import { startTransition, StrictMode, useEffect } from "react";
import { hydrateRoot } from "react-dom/client";
import * as Sentry from "@sentry/remix";
import posthog from "posthog-js";

const runtimeEnv = typeof window !== "undefined" ? window.__ENV : undefined;
const sentryDsn = runtimeEnv?.VITE_SENTRY_DSN ?? import.meta.env.VITE_SENTRY_DSN;
const posthogApiKey = runtimeEnv?.VITE_POSTHOG_API_KEY ?? import.meta.env.VITE_POSTHOG_API_KEY;

// Initialize Sentry if DSN is configured
if (typeof document !== 'undefined' && sentryDsn) {
    Sentry.init({
        dsn: sentryDsn,
        environment: import.meta.env.MODE,
        integrations: [
            Sentry.browserTracingIntegration({
                useEffect,
                useLocation,
                useMatches,
            }),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
        ],
        tracesSampleRate: 1.0,
        tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
    });
}

function PosthogInit() {
    useEffect(() => {
        if (posthogApiKey) {
            posthog.init(posthogApiKey, {
                api_host: 'https://eu.i.posthog.com',
                person_profiles: 'always',
            });
        }
    }, []);

    return null;
}

startTransition(() => {
    hydrateRoot(
        document,
        <StrictMode>
            <HydratedRouter />
            {posthogApiKey && <PosthogInit />}
        </StrictMode>
    );
});
